import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ContactModal from './ContactModal';
import BrochureModal from './BrochureModal';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isBrochureOpen, setIsBrochureOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { 
      label: language === 'fr' ? 'Akwaba' : 'Home', 
      href: '/',
      isLink: true 
    },
    { 
      label: language === 'fr' ? 'Explorer nos projets' : 'Explore our projects', 
      href: isHomePage ? '#projets' : '/#projets',
      isLink: false 
    },
    { 
      label: language === 'fr' ? 'Masterplan' : 'Masterplan', 
      href: isHomePage ? '#masterplan' : '/#masterplan',
      isLink: false 
    },
    { 
      label: language === 'fr' ? 'Galerie' : 'Gallery', 
      href: isHomePage ? '#galerie' : '/#galerie',
      isLink: false 
    },
    { 
      label: language === 'fr' ? 'ADN OGD' : 'Our DNA', 
      href: '/adn-ogd',
      isLink: true 
    }
  ];

  const handleNavClick = (e, href, isLink) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsMobileMenuOpen(false);
    } else if (isLink && href === '/' && location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-sm' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={isScrolled ? '/assets/logos/logo-dore.png' : '/assets/logos/logo-blanc.png'}
                alt="One Green Dev"
                className="h-[52px] w-auto object-contain transition-all duration-300"
                data-testid="header-logo"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-7">
              {navItems.map((item) => {
                const navClass = `relative text-[12.5px] font-medium tracking-wide transition-colors
                  after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[1.5px]
                  after:transition-all after:duration-300 hover:after:w-full
                  ${isScrolled 
                    ? 'text-gray-600 hover:text-emerald-800 after:bg-emerald-700' 
                    : 'text-white/85 hover:text-white after:bg-white/70'
                  }`;

                return item.isLink ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={(e) => handleNavClick(e, item.href, true)}
                    className={navClass}
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href, false)}
                    className={navClass}
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {item.label}
                  </a>
                );
              })}
            </nav>

            {/* Right Side - Language Switcher & CTA */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg transition-all text-[12px] ${
                  isScrolled 
                    ? 'bg-gray-100 hover:bg-gray-200 text-gray-600' 
                    : 'bg-white/10 hover:bg-white/20 text-white/80'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                aria-label="Switch language"
              >
                <Globe className="w-3.5 h-3.5" />
                <span className="font-medium uppercase">{language}</span>
              </button>

              {/* CTA: Recevoir la brochure */}
              <button
                onClick={() => setIsBrochureOpen(true)}
                className={`px-4 py-2 rounded text-[12px] font-medium tracking-wide transition-all border ${
                  isScrolled
                    ? 'border-emerald-700 text-emerald-700 hover:bg-emerald-50'
                    : 'border-white/35 text-white/90 hover:bg-white/10'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                data-testid="brochure-btn"
              >
                {language === 'fr' ? 'Recevoir la brochure' : 'Get brochure'}
              </button>

              {/* CTA: Échangeons */}
              <button
                onClick={() => setIsContactOpen(true)}
                className={`px-4 py-2 rounded text-[12px] font-medium tracking-wide transition-all ${
                  isScrolled
                    ? 'bg-emerald-700 text-white hover:bg-emerald-800'
                    : 'bg-white text-emerald-900 hover:bg-emerald-50'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                data-testid="contact-btn"
              >
                {language === 'fr' ? 'Échangeons' : 'Let\'s talk'}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className={`p-2 rounded-lg transition-all ${
                  isScrolled 
                    ? 'bg-gray-100 text-gray-700' 
                    : 'bg-white/10 text-white'
                }`}
              >
                <span className="text-xs font-bold uppercase">{language}</span>
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <nav className="max-w-7xl mx-auto px-6 py-4">
              {navItems.map((item) => (
                item.isLink ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={(e) => {
                      handleNavClick(e, item.href, true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="block py-3 text-gray-700 text-sm font-medium hover:text-emerald-600 transition-colors"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href, false)}
                    className="block py-3 text-gray-700 text-sm font-medium hover:text-emerald-600 transition-colors"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {item.label}
                  </a>
                )
              ))}
              <button
                onClick={() => { setIsMobileMenuOpen(false); setIsContactOpen(true); }}
                className="w-full mt-4 py-3 bg-emerald-700 text-white text-sm font-medium rounded-lg hover:bg-emerald-800 transition-colors"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {language === 'fr' ? 'Échangeons' : 'Let\'s talk'}
              </button>
              <button
                onClick={() => { setIsMobileMenuOpen(false); setIsBrochureOpen(true); }}
                className="w-full mt-2 py-3 border border-emerald-700 text-emerald-700 text-sm font-medium rounded-lg hover:bg-emerald-50 transition-colors text-center"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {language === 'fr' ? 'Recevoir la brochure' : 'Get brochure'}
              </button>
            </nav>
          </div>
        )}
      </header>

      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
      <BrochureModal isOpen={isBrochureOpen} onClose={() => setIsBrochureOpen(false)} />
    </>
  );
};

export default Header;
